<template>
  <div
    v-if="opcoesVisiveis.length"
    class="feature-tour-help"
    :class="{ 'is-icon-only': iconOnly, 'has-custom-icon-size': Boolean(iconFontSize) }"
    :style="rootCustomStyle"
  >
    <b-dropdown v-if="opcoesVisiveis.length > 1" position="is-bottom-left" aria-role="list">
      <button
        slot="trigger"
        type="button"
        class="button is-text is-small feature-tour-help__trigger"
        :class="triggerClass"
        :title="label"
        :aria-label="label"
      >
        <b-icon :icon="icon" :size="iconSize" :style="iconCustomStyle" />
        <span v-if="!iconOnly && label">{{ label }}</span>
      </button>
      <b-dropdown-item
        v-for="opcao in opcoesVisiveis"
        :key="opcao.id"
        aria-role="listitem"
        @click="$emit('selecionar', opcao.id)"
      >
        {{ opcao.label }}
      </b-dropdown-item>
    </b-dropdown>

    <button
      v-else
      type="button"
      class="button is-text is-small feature-tour-help__trigger"
      :class="triggerClass"
      :title="opcoesVisiveis[0].label"
      :aria-label="opcoesVisiveis[0].label"
      @click.prevent.stop="$emit('selecionar', opcoesVisiveis[0].id)"
    >
      <b-icon :icon="icon" :size="iconSize" :style="iconCustomStyle" />
      <span v-if="!iconOnly && label">{{ label }}</span>
    </button>
  </div>
</template>

<script>
import { listHelpOptions, resolveFeatureTourUserId } from '@/feature-tours';

export default {
  name: 'FeatureTourHelp',
  props: {
    /** @type {{ id: string, label: string, disponivel?: boolean }[]} */
    opcoes: {
      type: Array,
      default: () => [],
    },
    /** Contexto do registry (ex.: auction.recursos). Se informado, resolve opções automaticamente. */
    contexto: {
      type: String,
      default: null,
    },
    /** Contexto de runtime passado ao canStart (situation, logged, …). */
    featureCtx: {
      type: Object,
      default: () => ({}),
    },
    /** Escopo opcional dentro do contexto (ex.: form-razoes). */
    scope: {
      type: String,
      default: null,
    },
    label: {
      type: String,
      default: 'Ver tutorial',
    },
    triggerClass: {
      type: String,
      default: '',
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: 'help-circle-outline',
    },
    iconSize: {
      type: String,
      default: 'is-small',
    },
    /** Ex.: '1.45rem' — sobrescreve o tamanho visual do ícone */
    iconFontSize: {
      type: String,
      default: null,
    },
    /**
     * Se informado, só exibe opções cujo tutorial pode rodar agora.
     * @type {(tourId: string) => boolean}
     */
    verificarDisponibilidade: {
      type: Function,
      default: null,
    },
  },
  computed: {
    userId() {
      return resolveFeatureTourUserId(this.$store);
    },
    opcoesDoContexto() {
      if (!this.contexto || this.userId == null) return null;
      const ctx = {
        logged: Boolean(this.$store?.getters?.getLogged),
        ...this.featureCtx,
      };
      const options = {};
      if (this.scope) options.scope = this.scope;
      return listHelpOptions(this.contexto, this.userId, ctx, options);
    },
    opcoesBase() {
      return this.opcoesDoContexto != null ? this.opcoesDoContexto : this.opcoes || [];
    },
    opcoesVisiveis() {
      return (this.opcoesBase || []).filter(opcao => {
        if (!opcao || opcao.disponivel === false) return false;
        if (typeof this.verificarDisponibilidade === 'function') {
          return Boolean(this.verificarDisponibilidade(opcao.id));
        }
        return true;
      });
    },
    rootCustomStyle() {
      if (!this.iconFontSize) return null;
      return { '--ft-icon-size': this.iconFontSize };
    },
    iconCustomStyle() {
      if (!this.iconFontSize) return null;
      return {
        fontSize: this.iconFontSize,
        width: this.iconFontSize,
        height: this.iconFontSize,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.feature-tour-help {
  display: inline-flex;
  align-items: center;
}

.feature-tour-help__trigger {
  height: auto;
  padding-left: 0.35rem;
  padding-right: 0.35rem;
  text-decoration: none;

  .icon {
    margin-right: 0.25rem;
  }
}

.feature-tour-help.is-icon-only .feature-tour-help__trigger {
  padding: 0.2rem;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
  transition: transform 0.15s ease, opacity 0.15s ease;

  .icon {
    margin-right: 0;
  }

  &:hover {
    transform: scale(1.12);
    opacity: 0.9;
  }
}

.feature-tour-help.has-custom-icon-size ::v-deep .icon {
  font-size: var(--ft-icon-size) !important;
  width: var(--ft-icon-size) !important;
  height: var(--ft-icon-size) !important;
}

.feature-tour-help.has-custom-icon-size ::v-deep .icon i,
.feature-tour-help.has-custom-icon-size ::v-deep .mdi:before {
  font-size: var(--ft-icon-size) !important;
  line-height: 1 !important;
}
</style>
